import * as React from 'react'

import * as countries from '@owl-nest/countries'
import * as hooks from '@owl-nest/hooks'
import * as utils from '@owl-nest/utils'
import * as plume from '@ulule/owl-kit-components/next'

type ShippingCountriesProps = {
  selectedCountry: string | undefined
  setSelectedCountry: (countryCode: string) => void
  supportedCountries: string[] | undefined
}

export function ShippingCountries({
  selectedCountry,
  setSelectedCountry,
  supportedCountries,
}: ShippingCountriesProps): React.ReactElement<ShippingCountriesProps> {
  const previouslySupportedCountries = hooks.usePrevious(supportedCountries)
  const [sortedCountries, setSortedCountries] = React.useState<string[] | undefined>(supportedCountries)

  const joinPreviouslySupportedCountries = previouslySupportedCountries?.join()
  const joinSupportedCountries = supportedCountries?.join()

  React.useEffect(() => {
    if (!supportedCountries) {
      return
    }

    if (!previouslySupportedCountries || supportedCountries.join() !== previouslySupportedCountries.join()) {
      const sortedCountryList = supportedCountries
        .filter((countryCode) => countries.frenchCountryList[countryCode])
        .sort((current, previous) => {
          const currentCountryName = utils.text.slugify(countries.frenchCountryList[current])
          const previousCountryName = utils.text.slugify(countries.frenchCountryList[previous])
          if (currentCountryName < previousCountryName) {
            return -1
          } else if (currentCountryName > previousCountryName) {
            return 1
          }
          return 0
        })
      setSortedCountries(sortedCountryList)
    }
  }, [joinPreviouslySupportedCountries, joinSupportedCountries])

  return (
    <plume.Select
      native
      height={300}
      onChange={(selected) => {
        setSelectedCountry(selected[0].value)
      }}
      placeholder="Choisissez un pays"
      selected={[selectedCountry] ?? ''}
      filterable
    >
      {sortedCountries?.map((countryCode) => {
        const countryName = countries.frenchCountryList[countryCode]

        // ignore unknown country code to avoid passing an `undefined` label to
        // the option (that will trigger an error in the `filterable` feature
        // that try to `lowercase()` the undefined label)
        if (countryName === undefined) {
          return <></>
        }

        return (
          <plume.Option native={true} key={countryCode} value={countryCode} label={countryName}>
            {countryName}
          </plume.Option>
        )
      })}
    </plume.Select>
  )
}
